import {
  AbstractFilterWidget,
  AbstractFilterWidgetOperatorTrigger,
  AbstractFilterWidgetValueTrigger,
  FilterWidgetPropsBase
} from './_private/AbstractFilterWidget';
import { forwardRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from 'v5/design-system/Select';
import { useTranslation } from 'react-i18next';
import { BooleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';

export interface BooleanFilterWidgetProps
  extends FilterWidgetPropsBase<BooleanFilter> {
  /**
   * Domain-specific label to render in the value slot, e.g. "archived"
   */
  valueLabel: string;
  /**
   * Domain-specific label to render in the subject slot, e.g. "shortlist"
   */
  subjectLabel: string;
  filterField?: string;
}

const BooleanFilterWidgetInner = (
  {
    filterName,
    filterIcon,
    onRemove,
    value,
    onValueChange,
    valueLabel,
    filterField,
    subjectLabel,
    ...rest
  }: BooleanFilterWidgetProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const { t } = useTranslation('patterns');
  return (
    <AbstractFilterWidget
      ref={ref}
      filterName={subjectLabel}
      filterIcon={filterIcon}
      onRemove={onRemove}
      filterField={filterField}
      {...rest}
      operatorSlot={
        <Select
          value={value.operator}
          onValueChange={next =>
            onValueChange({
              operator: 'equal',
              value: next === 'is' ? true : false
            })
          }
        >
          <SelectTrigger asChild>
            <AbstractFilterWidgetOperatorTrigger data-element="operator-trigger">
              {value.value
                ? t('BooleanFilterWidget.operator.is')
                : t('BooleanFilterWidget.operator.isNot')}
            </AbstractFilterWidgetOperatorTrigger>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="is">
              {t('BooleanFilterWidget.operator.is')}
            </SelectItem>
            <SelectItem value="isNot">
              {t('BooleanFilterWidget.operator.isNot')}
            </SelectItem>
          </SelectContent>
        </Select>
      }
      valueSlot={
        <AbstractFilterWidgetValueTrigger
          aria-readonly
          className="cursor-default"
          data-element="value-trigger"
        >
          {valueLabel}
        </AbstractFilterWidgetValueTrigger>
      }
    />
  );
};

export const BooleanFilterWidget = forwardRef(BooleanFilterWidgetInner) as (
  props: BooleanFilterWidgetProps & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof BooleanFilterWidgetInner>;
